import { useEffect, useState, Suspense, useCallback } from 'react';

import LinearProgress from '@zenbusiness/zui/LinearProgress';
import { useNavigate } from 'react-router-dom';

import OpenModals from '#client/components/OpenModals';
import { RELOAD_COMBINED_PROVIDER } from '#client/constants/eventName';
import useBankingPath from '#client/hooks/useBankingPath';
import { CardsProvider } from '#client/hooks/useCardsContext';
import { CheckTransactionProvider } from '#client/hooks/useCheckTransactionContext';
import useConstBusinessEntityUuid from '#client/hooks/useConstBusinessEntityUuid';
import { DepositProvider } from '#client/hooks/useDepositContext';
import { DisableDashboardProvider } from '#client/hooks/useDisableDashboardContext';
import useEventListeners from '#client/hooks/useEventListeners';
import { ModalProvider } from '#client/hooks/useModalContext';
import { NextUpCardsProvider } from '#client/hooks/useNextUpCardsContext';
import { SubScriptionProvider } from '#client/hooks/useSubscriptionContext';
import { UnitAccountProvider } from '#client/hooks/useUnitAccount';
import { combineContexts } from '#client/utils/combineContext';
import { dispatchReloadCombinedProvider } from '#client/utils/event';

import { AstraProfileProvider } from './useAstraProfileContext';
import { BannerProvider } from './useBannerContext';
import { WhatsNewBannerProvider } from './useWhatNewBannerContext';

const DELAY_CHANGE_BUSINESS_ENTITY = 1000;

const orderedProviders = [
  SubScriptionProvider,
  BannerProvider,
  UnitAccountProvider,
  AstraProfileProvider,
  CheckTransactionProvider,
  DisableDashboardProvider,
  CardsProvider,
  DepositProvider,
  NextUpCardsProvider,
  WhatsNewBannerProvider,
  ModalProvider
];

const CombinedProvider = combineContexts(...orderedProviders);

const CombinedDashboardProvider = ({ children }) => {
  const { businessEntityUuid } = useConstBusinessEntityUuid();
  const navigate = useNavigate();
  const [reload, setReload] = useState(false);
  const [currentBusinessEntityUuid, setCurrentBusinessEntityUuid] = useState(businessEntityUuid);
  const bankingPath = useBankingPath();

  const reloadCombinedProvider = useCallback(() => {
    setReload(true);

    const timer = setTimeout(() => {
      setReload(false);
    }, DELAY_CHANGE_BUSINESS_ENTITY);

    return timer;
  }, []);

  /**
   * We listen to the change of the businessEntityUuid parameter and reload the page
   */
  useEffect(
    () => {
      if (businessEntityUuid !== currentBusinessEntityUuid) {
        setCurrentBusinessEntityUuid(businessEntityUuid);
        navigate(`${bankingPath}/dashboard`);
        dispatchReloadCombinedProvider();
      }
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [businessEntityUuid]
  );

  useEventListeners([
    {
      eventName: RELOAD_COMBINED_PROVIDER,
      eventHandler: reloadCombinedProvider
    }
  ]);

  if (reload) {
    return <LinearProgress aria-label="progresbar" />;
  }

  return (
    <Suspense fallback={<LinearProgress aria-label="progresbar" />}>
      <CombinedProvider>
        {children}
        <OpenModals />
      </CombinedProvider>
    </Suspense>
  );
};

export default CombinedDashboardProvider;
